import React, { useCallback, useEffect } from "react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { Plus, Wallet } from "lucide-react";
import { finApi } from "@/lib/financeApi";
import { Chip, ErrorBox, FinRoster, Loading, PAYCHECK_CHIP, useLoad, useView } from "@/components/nds/windows/finance/parts";
import FinPaycheckDetail from "@/components/nds/windows/finance/FinPaycheckDetail";

export default function FinPaycheckContent({ win }) {
  const [view, setView] = useView("paycheck_view");
  const [selected, setSelected] = React.useState(null);
  const [state, load] = useLoad(() => Promise.all([finApi.get("/residents", { status: view }), finApi.get("/paychecks")])
    .then(([r, p]) => {
      const latest = {};
      p.paychecks.forEach((x) => { if (!latest[x.resident_id]) latest[x.resident_id] = x; });
      return { ...r, latest };
    }), [view]);

  const { openWindow } = useFloatingWindows();
  const focusId = win?.payload?.residentId;
  useEffect(() => {
    const r = focusId && state.data?.residents?.find((x) => x.id === focusId);
    if (r) setSelected(r);
  }, [focusId, win?.payload?.nonce, state.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = useCallback(() => load(), [load]);
  useEffect(() => { window.addEventListener("fin:paycheck-logged", refresh); return () => window.removeEventListener("fin:paycheck-logged", refresh); }, [refresh]);
  if (selected) return <FinPaycheckDetail resident={selected} onBack={() => { setSelected(null); load(); }} />;
  return (
    <div data-testid="fin-paycheck-content" className="flex flex-col h-full">
      {state.loading && !state.data ? <Loading testid="fin-paycheck-loading" label="Loading paychecks…" />
        : state.error ? <ErrorBox testid="fin-paycheck-error" error={state.error} onRetry={load} />
        : (
          <FinRoster prefix="fin-paycheck" icon={Wallet} view={view} setView={setView} counts={state.data.counts}
            residents={state.data.residents} onOpen={setSelected} onRefresh={load} dateOf={(r) => state.data.latest[r.id]?.pay_period_end}
            rowAction={(r) => (
              <button type="button" data-testid={`fin-paycheck-log-${r.id}`}
                onClick={() => openWindow("fin_paycheck_form", { title: `Log paycheck · ${r.first_name} ${r.last_name}`, residentId: r.id })}
                aria-label="Log paycheck" title="Log paycheck"
                className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1.5 border border-nex-gold/50 text-nex-gold hover:bg-nex-gold/10"><Plus className="h-3.5 w-3.5" /><span className="hidden sm:inline">Log paycheck</span></button>
            )}
            renderRight={(r) => {
              const pc = state.data.latest[r.id];
              const [tone, label] = PAYCHECK_CHIP[pc?.status || "none"];
              return (
                <>
                  {pc && <span className="font-mono text-[10px] text-nex-muted">{pc.pay_period_end}</span>}
                  <Chip tone={tone} data-testid={`fin-paycheck-chip-${r.id}`}>{label}</Chip>
                </>
              );
            }} />
        )}
    </div>
  );
}
